// The live 3D track (shared/racing/_stage): Grand Prix or Endurance.
// Polls the feed, races the cars in running order, sends them through the
// pit lane now and then, and drives the timing tower. Endurance adds the
// class filter and a slow day/night cycle with headlights and floodlights.
// Respects prefers-reduced-motion (no auto-rotate, starts paused, fixed dusk).
import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { layouts } from '../lib/track/layouts'
import { buildCircuit, FONT, type Circuit } from '../lib/track/scene'
import { buildCarModel, carTypeFor, type CarModel, type CarType } from '../lib/cars'
import { buildDriver, type Driver } from '../lib/cars/driver'
import { dress, dressCircuit, glbCarFrom, loadModel, type CircuitExtras } from '../lib/models'
import type { Livery } from '../lib/cars/livery'
import { setMine, setVoted } from '../lib/vote'
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js'
import {
  BASE_LAPS_PER_SECOND, classLabel, daylight, filterByClass, focusedId, gapLabel, lapOffsets, pitsOnLap, speedFactor, toggleFocus, wrap,
  type CameraMode, type Car, type Feed, type Focus,
} from '../lib/track/race'

const stage = document.querySelector<HTMLElement>('[data-track]')
if (stage) void start(stage)

type RaceCar = {
  car: Car
  built: CarModel
  type: CarType
  key: string
  mesh: THREE.Group
  label: THREE.Sprite
  offset: number
  lag: number
  phase: number
  lane: number
  garage: number
  pittedLap: number
  pit: { s: number; stopped: number } | null
  u: number
  forward: THREE.Vector3
}

async function start(stage: HTMLElement) {
  const mode = stage.dataset.track === 'endurance' ? 'endurance' : 'grand_prix'
  const layout = layouts[mode]
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const wrapEl = stage.closest('.stage-wrap') ?? document
  const towerRows = stage.querySelector<HTMLOListElement>('[data-tower-rows]')!
  const flag = stage.querySelector<HTMLElement>('[data-flag]')
  const flagText = stage.querySelector<HTMLElement>('[data-flag-text]')

  await Promise.race([
    Promise.all([document.fonts?.load(`800 80px ${FONT}`), document.fonts?.load(`600 40px ${FONT}`)]),
    new Promise((resolve) => setTimeout(resolve, 1500)),
  ]).catch(() => undefined)

  let feed: Feed
  try {
    feed = await fetchFeed(stage.dataset.feed!)
  } catch {
    return // the server-rendered tower stays
  }
  // Development: ?cars=N races only the first N cars.
  const debugCars = import.meta.env.DEV ? Number(new URL(location.href).searchParams.get('cars')) : 0
  if (debugCars) feed.cars = feed.cars.slice(0, debugCars)

  // ── Renderer, scene, camera ────────────────────────────────────────────
  let renderer: THREE.WebGLRenderer
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true })
  } catch {
    stage.classList.add('stage-no-webgl')
    return
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.PCFShadowMap
  renderer.toneMapping = THREE.NeutralToneMapping
  renderer.domElement.setAttribute('aria-label', mode === 'endurance' ? 'LaunchGP Endurance live track' : 'Grand Prix live track')
  renderer.domElement.setAttribute('role', 'img')
  stage.insertBefore(renderer.domElement, stage.firstChild)

  const scene = new THREE.Scene()
  // Glossy arcade paint needs something to reflect.
  scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(), 0.04).texture
  scene.environmentIntensity = 0.6
  // The Grand Prix races on a sunny afternoon; Endurance runs its own day/night.
  const sky = new THREE.Color(mode === 'grand_prix' ? 0x8ec9f0 : 0x10141f)
  scene.background = sky
  // One pit garage per product category.
  const categories: string[] = JSON.parse(stage.dataset.categories || '[]')
  const garageCount = Math.max(1, categories.length)
  // Both tracks use the Blender models (public/models): the circuit is still
  // computed here for the race logic (curve, pit lane, garages), but only
  // the Blender circuit is drawn. Falls back to the procedural one.
  const carTypes: CarType[] = mode === 'grand_prix' ? ['formula'] : ['rookie', 'gt', 'prototype', 'hypercar']
  let templates: { cars: Partial<Record<CarType, THREE.Group>>; pilot: THREE.Group } | null = null
  let circuitModel: THREE.Group | null = null
  try {
    const [pilot, track, ...cars] = await Promise.all([
      loadModel('driver'), loadModel(mode === 'grand_prix' ? 'circuit_gp' : 'circuit_endurance'), ...carTypes.map((t) => loadModel(t)),
    ])
    templates = { pilot, cars: Object.fromEntries(carTypes.map((t, i) => [t, cars[i]])) }
    circuitModel = track.clone(true)
  } catch {
    templates = null
  }
  const circuit = buildCircuit(templates ? new THREE.Scene() : scene, layout, garageCount)
  let extras: CircuitExtras | null = null
  if (circuitModel) {
    const signOf = (s: { name: string; logo?: string | null; art?: string | null } | null | undefined) => (s ? { text: s.name, logo: s.logo ?? null, art: s.art ?? null } : 'YOUR BRAND HERE')
    extras = dressCircuit(circuitModel, {
      banners: [feed.round?.title ?? 'LaunchGP Endurance', ...(feed.banners ? feed.banners.map(signOf) : feed.billboards.map(signOf))],
      billboards: feed.billboards.map(signOf),
      categories,
      ...(mode === 'endurance' ? { bridge: feed.bridge ? signOf(feed.bridge) : null, ferris: feed.ferris ? signOf(feed.ferris) : null } : {}),
    })
    scene.add(circuitModel)
  }
  // Floodlights for the night stints: real lights at the Blender poles.
  const floodlights = extras
    ? extras.lampPoints.slice(0, 12).map((p) => {
        const light = new THREE.PointLight(0xfff1d6, 0, 140, 1.6)
        light.position.copy(p).add(new THREE.Vector3(0, -1.5, 0))
        scene.add(light)
        return light
      })
    : circuit.floodlights
  const trackLamps = extras ? extras.lamps : circuit.lamps
  const makeCar = (type: CarType, livery: Livery, driver?: Driver): CarModel => {
    const template = templates?.cars[type]
    return templates && template ? (glbCarFrom(template, templates.pilot, livery, driver) as unknown as CarModel) : buildCarModel(type, livery, driver)
  }
  const far = circuit.size
  scene.fog = new THREE.Fog(sky.getHex(), far * 0.7, far * 1.8)

  const centre = new THREE.Box3().setFromPoints(circuit.samples.map((s) => s.p)).getCenter(new THREE.Vector3())
  const camera = new THREE.PerspectiveCamera(42, 1, 0.5, far * 4)
  const broadcastPos = centre.clone().add(new THREE.Vector3(-far * 0.15, far * 0.45, far * 0.6))
  camera.position.copy(broadcastPos)

  const controls = new OrbitControls(camera, renderer.domElement)
  controls.target.copy(centre)
  controls.enableDamping = true
  controls.maxPolarAngle = 1.38
  controls.minDistance = 8
  controls.maxDistance = far * 1.4
  controls.autoRotate = !reduced
  controls.autoRotateSpeed = 0.3

  const hemi = new THREE.HemisphereLight(0xcfe6ff, 0x3a5a2a, mode === 'grand_prix' ? 1.3 : 0.9)
  scene.add(hemi)
  const sun = new THREE.DirectionalLight(0xffd6ad, 1.6)
  sun.position.copy(centre).add(new THREE.Vector3(far * 0.3, far * 0.5, far * 0.2))
  sun.target.position.copy(centre)
  sun.castShadow = true
  sun.shadow.mapSize.set(2048, 2048)
  Object.assign(sun.shadow.camera, { left: -far / 2, right: far / 2, top: far / 2, bottom: -far / 2, near: 10, far: far * 2 })
  scene.add(sun, sun.target)

  circuit.setBillboards(feed.billboards)
  if (feed.round) circuit.setBanner(feed.round.title, feed.round.sponsor ? `sponsored by ${feed.round.sponsor.name}` : 'start / finish')
  else circuit.setBanner('LAUNCHGP ENDURANCE', 'the race that never ends')

  // ── Cars ──────────────────────────────────────────────────────────────
  const cars = new Map<number, RaceCar>()
  categories.forEach((category, i) => circuit.setGarageLabel(i, category, '', '#FF5A1F'))

  function garageFor(car: Car): number {
    const i = categories.indexOf(car.category ?? '')
    return i >= 0 ? i : categories.length - 1
  }

  function syncCars(next: Feed) {
    const offsets = lapOffsets(next)
    const seen = new Set<number>()
    next.cars.forEach((car, index) => {
      seen.add(car.id)
      const color = mode === 'endurance' ? car.class_color ?? car.livery : car.livery
      const type = carTypeFor(mode, car.car_class)
      const livery = liveryFor(car)
      const key = `${type}|${JSON.stringify(livery)}|${JSON.stringify(car.driver ?? {})}`
      let rc = cars.get(car.id)
      if (rc && rc.key !== key) {
        // New livery or class: swap the model, keep its place in the race.
        scene.remove(rc.mesh)
        rc.built.dispose()
        rc.built = makeCar(type, livery, car.driver as Driver | undefined)
        rc.mesh = rc.built.group
        rc.type = type
        rc.key = key
        scene.add(rc.mesh)
      }
      if (!rc) {
        const built = makeCar(type, livery, car.driver as Driver | undefined)
        const label = labelSprite(car.name, color)
        scene.add(built.group, label)
        rc = {
          car, built, type, key, mesh: built.group, label, offset: offsets.get(car.id) ?? 0, lag: 0,
          phase: Math.random() * 10, lane: (index % 2 ? -1 : 1) * 2.2, garage: 0, pittedLap: -1, pit: null, u: 0, forward: new THREE.Vector3(0, 0, 1),
        }
        cars.set(car.id, rc)
      }
      rc.car = car
      rc.offset = offsets.get(car.id) ?? rc.offset
      rc.garage = garageFor(car)
    })
    for (const [id, rc] of cars) {
      if (seen.has(id)) continue
      scene.remove(rc.mesh, rc.label)
      cars.delete(id)
    }
  }
  syncCars(feed)

  // Drivers waiting in front of their category's garage (Blender models).
  if (templates) {
    const shown = new Set<number>()
    for (const car of feed.cars) {
      const g = garageFor(car)
      if (shown.has(g) || !car.driver) continue
      shown.add(g)
      const garage = circuit.garages[g]
      if (!garage) continue
      const figure = dress(templates.pilot, liveryFor(car), car.driver as never)
      figure.position.copy(garage.box).addScaledVector(garage.facing, 2.6)
      figure.lookAt(figure.position.clone().sub(garage.facing))
      scene.add(figure)
    }
  }

  // ── Timing tower ──────────────────────────────────────────────────────
  let focus: Focus = { kind: 'broadcast' }
  let classFilter = 'all'

  // The front-row ticker (bought slots by grid position; never the running order).
  const ticker = wrapEl.querySelector<HTMLElement>('[data-front-row]')
  function renderFrontRow() {
    if (!ticker) return
    const front = feed.cars.filter((c) => c.kind === 'paid').sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0))
    ticker.hidden = front.length === 0
    const items = () => front.map((c) => {
      const el = document.createElement(c.url ? 'a' : 'span')
      el.className = 'front-row-item'
      const host = c.url ? new URL(c.url).hostname.replace(/^www\./, '') : ''
      const icon = c.paint?.logo ?? (host ? `https://icons.duckduckgo.com/ip3/${host}.ico` : null)
      if (icon) {
        const img = document.createElement('img')
        img.src = icon
        img.alt = ''
        img.className = 'front-row-icon'
        img.loading = 'lazy'
        img.onerror = () => img.remove()
        el.append(img, ' ')
      }
      el.append(`P${c.slot} · ${c.name.toUpperCase()}`)
      if (el instanceof HTMLAnchorElement && c.url) {
        el.href = c.url
        el.target = '_blank'
        el.rel = 'sponsored noopener'
        el.dataset.click = c.slug ?? ''
        el.dataset.clickSource = 'ticker'
      }
      return el
    })
    for (const list of ticker.querySelectorAll<HTMLElement>('.front-row-items')) list.replaceChildren(...items())
  }
  function renderTower() {
    renderFrontRow()
    const list = mode === 'endurance' ? filterByClass(feed.cars, classFilter) : feed.cars
    const selected = focusedId(focus)
    towerRows.replaceChildren(
      ...list.map((car, index) => {
        const li = document.createElement('li')
        li.className = 'tower-row'
        li.dataset.car = String(car.id)
        const button = document.createElement('button')
        button.type = 'button'
        button.dataset.focus = String(car.id)
        button.setAttribute('aria-pressed', String(selected === car.id))
        button.setAttribute('aria-label', `${car.name}: ${selected === car.id && focus.kind === 'car' ? 'show its pit box' : 'follow on track'}`)
        const pos = span('pos', String(mode === 'endurance' ? (classFilter === 'all' ? car.overall : car.class_position) : index + 1))
        const bar = span('tower-bar', '')
        bar.style.background = mode === 'endurance' ? car.class_color ?? car.livery : car.livery
        const name = span('nm', car.name.toUpperCase())
        const gap = span('gap', mode === 'endurance' ? (classFilter === 'all' ? classLabel(car) : `P${car.overall} overall`) : gapLabel(car, index))
        button.append(pos, bar, name, gap)
        li.append(button)
        return li
      }),
    )
    const meta = stage.querySelector<HTMLElement>('[data-tower-meta]')
    if (meta) meta.textContent = mode === 'endurance' ? (classFilter === 'all' ? 'OVERALL' : 'CLASS') : 'GAP'
  }
  renderTower()

  towerRows.addEventListener('click', (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>('[data-focus]')
    if (!button) return
    setFocus(toggleFocus(focus, Number(button.dataset.focus)))
  })

  wrapEl.querySelector('[data-class-filter]')?.addEventListener('click', (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>('[data-class]')
    if (!button) return
    classFilter = button.dataset.class!
    wrapEl.querySelectorAll<HTMLButtonElement>('[data-class]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)))
    for (const rc of cars.values()) {
      const visible = classFilter === 'all' || rc.car.car_class === classFilter
      rc.label.visible = visible
      rc.mesh.visible = visible
    }
    renderTower()
    // The cameras switch to the class leader (the frame loop reads classFilter); refresh the hint.
    if (focus.kind === 'broadcast' || focus.kind === 'chase') setFocus(focus)
  })

  // ── Cameras ───────────────────────────────────────────────────────────
  // Broadcast: fixed trackside cameras that pan and zoom on the leader, cutting
  // to the next one as the car goes by. Chase: follows the leader from the
  // front-right, drag to orbit around it. Free: WASD or drag to move.
  const camSelect = wrapEl.querySelector<HTMLSelectElement>('[data-cam-select]')
  const hint = stage.querySelector<HTMLElement>('[data-stage-hint]')
  let camChoice: CameraMode = 'broadcast'
  const baseFov = camera.fov
  const followOffset = new THREE.Vector3() // camera offset in the followed car's frame (x right, y up, z forward)
  const stations: Station[] = broadcastStations(circuit.curve, centre, layout.width, circuit.garages.map((g) => g.box), circuit.pitCurve, layout.startU, layout.stands)
  let station = 0
  let stationHeld = 0

  function setFocus(next: Focus) {
    const was = focus
    focus = next
    if (next.kind === 'broadcast' || next.kind === 'chase' || next.kind === 'free') camChoice = next.kind
    if (camSelect) camSelect.value = next.kind === 'car' || next.kind === 'pit' ? 'driver' : next.kind
    const following = next.kind === 'chase' || next.kind === 'car'
    controls.enabled = next.kind !== 'broadcast'
    controls.autoRotate = false
    controls.enablePan = next.kind === 'free'
    controls.screenSpacePanning = false
    controls.mouseButtons.LEFT = next.kind === 'free' ? THREE.MOUSE.PAN : THREE.MOUSE.ROTATE
    controls.mouseButtons.RIGHT = next.kind === 'free' ? THREE.MOUSE.ROTATE : THREE.MOUSE.PAN
    controls.minDistance = following ? 4 : 8
    controls.maxDistance = following ? 60 : far * 1.4
    if (next.kind !== 'broadcast' && camera.fov !== baseFov) {
      camera.fov = baseFov
      camera.updateProjectionMatrix()
    }
    if (following && !(was.kind === 'chase' || was.kind === 'car')) followOffset.set(7, 3.2, 8.5)
    if (next.kind === 'broadcast') stationHeld = 0
    else if (next.kind === 'free' && was.kind === 'broadcast') {
      camera.position.copy(broadcastPos)
      controls.target.copy(centre)
    } else if (next.kind === 'pit') {
      const rc = cars.get(next.id)
      const garage = circuit.garages[rc?.garage ?? 0]
      if (garage) {
        const look = garage.front.clone().addScaledVector(garage.facing, 3).setY(1.5)
        camera.position.copy(garage.box).addScaledVector(garage.facing, -14).setY(7)
        controls.target.copy(look)
      }
    }
    if (hint) {
      hint.textContent = next.kind === 'broadcast' ? (classFilter === 'all' ? 'Trackside cameras follow the leader' : 'Trackside cameras follow the class leader')
        : next.kind === 'free' ? 'WASD or drag to move · right-drag to turn · scroll to zoom'
        : 'Drag to orbit · scroll to zoom'
    }
    if (!following) controls.update()
    renderTower()
    const id = focusedId(next)
    const followed = id === null ? undefined : feed.cars.find((c) => c.id === id)
    if (followed) card.show(followed, feed.round?.state === 'live')
    else card.hide()
  }
  const card = driverCard(stage, mode, () => setFocus({ kind: camChoice }), templates?.pilot ?? null)
  camSelect?.addEventListener('change', () => {
    if (camSelect.value !== 'driver') setFocus({ kind: camSelect.value as CameraMode })
  })

  // Free camera: WASD moves over the ground (while the track is on screen).
  const keys = new Set<string>()
  let stageVisible = true
  new IntersectionObserver(([entry]) => (stageVisible = entry.isIntersecting)).observe(stage)
  window.addEventListener('keydown', (e) => {
    if (focus.kind !== 'free' || !stageVisible || e.metaKey || e.ctrlKey || e.altKey) return
    if (e.target instanceof Element && e.target.closest('input, textarea, select, [contenteditable]')) return
    const key = e.key.toLowerCase()
    if (!['w', 'a', 's', 'd'].includes(key)) return
    keys.add(key)
    e.preventDefault()
  })
  window.addEventListener('keyup', (e) => keys.delete(e.key.toLowerCase()))
  window.addEventListener('blur', () => keys.clear())

  // Click a car on track to follow it.
  const raycaster = new THREE.Raycaster()
  const pointer = new THREE.Vector2()
  let downAt = { x: 0, y: 0 }
  renderer.domElement.addEventListener('pointerdown', (e) => (downAt = { x: e.clientX, y: e.clientY }))
  renderer.domElement.addEventListener('pointerup', (e) => {
    if (Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y) > 5) return // a drag, not a click
    const rect = renderer.domElement.getBoundingClientRect()
    pointer.set(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1)
    raycaster.setFromCamera(pointer, camera)
    const hit = raycaster.intersectObjects([...cars.values()].map((rc) => rc.mesh), true)[0]
    if (!hit) return
    let node: THREE.Object3D | null = hit.object
    while (node && ![...cars.values()].some((c) => c.mesh === node)) node = node.parent
    const rc = [...cars.values()].find((c) => c.mesh === node)
    if (rc) setFocus({ kind: 'car', id: rc.car.id })
  })

  let playing = !reduced
  const playButton = wrapEl.querySelector<HTMLButtonElement>('[data-play]')
  const syncPlay = () => {
    if (!playButton) return
    playButton.dataset.playing = String(playing)
    playButton.setAttribute('aria-label', playing ? 'Pause' : 'Play')
  }
  playButton?.addEventListener('click', () => {
    playing = !playing
    syncPlay()
  })
  syncPlay()

  function resize() {
    const w = stage.clientWidth
    const h = stage.clientHeight
    renderer.setSize(w, h, false)
    camera.aspect = w / Math.max(h, 1)
    camera.updateProjectionMatrix()
  }
  new ResizeObserver(resize).observe(stage)
  resize()

  // ── Feed polling ──────────────────────────────────────────────────────
  setInterval(async () => {
    if (document.hidden) return
    try {
      feed = await fetchFeed(stage.dataset.feed!)
      syncCars(feed)
      renderTower()
    } catch {
      // keep racing on the last data
    }
  }, mode === 'endurance' ? 60_000 : 30_000)

  // A vote cast on this page (vote.ts): refresh the order now, past the feed's short cache.
  document.addEventListener('launchgp:vote', async () => {
    try {
      feed = await fetchFeed(`${stage.dataset.feed!}${stage.dataset.feed!.includes('?') ? '&' : '?'}v=${Date.now()}`)
      syncCars(feed)
      renderTower()
    } catch {
      // the next poll catches up
    }
  })

  if (feed.round && feed.round.state === 'finished' && flag && flagText) {
    flagText.textContent = `Chequered flag · ${feed.cars[0]?.name ?? ''} wins ${feed.round.title}`
    flag.hidden = false
  }

  // ── Loop ──────────────────────────────────────────────────────────────
  const base = BASE_LAPS_PER_SECOND[mode]
  let clock = 0
  let last = performance.now()
  const camGoal = new THREE.Vector3()
  const camLook = new THREE.Vector3()
  const tmp = new THREE.Vector3()
  const pitLaneLength = circuit.pitCurve.getLength()
  const pitSpeed = 0.42 // fraction of normal speed in the pit lane
  const trackLength = circuit.curve.getLength()

  function stepCar(rc: RaceCar, dt: number) {
    const leaderDistance = clock * base
    const surge = Math.sin(clock * 0.6 + rc.phase) * 0.003
    const distance = leaderDistance * speedFactor(rc.car, mode) / speedFactor(feed.cars[0], mode) - rc.offset - rc.lag + surge
    const u = wrap(distance)
    const lap = Math.floor(distance)

    if (rc.pit) {
      // In the pit lane: drive to the box, stop, drive out.
      const garage = circuit.garages[rc.garage]
      const boxS = garage ? pitParamNear(circuit, garage.box) : 0.5
      if (rc.pit.stopped > 0) {
        rc.pit.stopped -= dt
      } else {
        const before = rc.pit.s
        rc.pit.s += (dt * base * trackLength * pitSpeed) / pitLaneLength
        if (before < boxS && rc.pit.s >= boxS) {
          rc.pit.s = boxS
          rc.pit.stopped = mode === 'endurance' ? 4 : 2.5
        }
      }
      // Time spent here is lost on the track.
      rc.lag += dt * base * (1 - (rc.pit.stopped > 0 ? 0 : pitSpeed))
      if (rc.pit.s >= 1) {
        rc.pit = null
      } else {
        const s = Math.min(rc.pit.s, 1)
        const p = circuit.pitCurve.getPointAt(s)
        const t = circuit.pitCurve.getTangentAt(s)
        if (rc.pit.stopped > 0 && garage) {
          // Parked nose-in at the box.
          rc.mesh.position.copy(garage.box)
          tmp.copy(garage.box).add(garage.facing)
        } else {
          rc.mesh.position.copy(p)
          tmp.copy(p).add(t)
          spin(rc, dt * base * trackLength * pitSpeed)
        }
        rc.mesh.lookAt(tmp)
        rc.forward.copy(t)
        rc.label.position.set(rc.mesh.position.x, 3.1, rc.mesh.position.z)
        return
      }
    }

    // Catch the pit loss back slowly over the following laps.
    if (rc.lag > 0) rc.lag = Math.max(0, rc.lag - dt * base * 0.08)

    // Entering the pit lane?
    const entry = circuit.pitEntryU
    if (rc.pittedLap !== lap && crossed(rc.u, u, entry) && pitsOnLap(rc.car, lap, mode)) {
      rc.pittedLap = lap
      rc.pit = { s: 0, stopped: 0 }
    }
    rc.u = u

    const p = circuit.curve.getPointAt(u)
    const t = circuit.curve.getTangentAt(u)
    const n = tmp.set(-t.z, 0, t.x)
    const lane = rc.lane * Math.cos(clock * 0.35 + rc.phase) + Math.sin(clock * 0.9 + rc.phase) * 0.5
    rc.mesh.position.copy(p).addScaledVector(n, lane)
    rc.mesh.lookAt(rc.mesh.position.clone().add(t))
    spin(rc, dt * base * trackLength)
    rc.forward.copy(t)
    rc.label.position.set(rc.mesh.position.x, 3.1, rc.mesh.position.z)
  }

  // Endurance: one day every 4 minutes, fixed dusk with reduced motion.
  const daySky = new THREE.Color(0x8fb2d8)
  const nightSky = new THREE.Color(0x05070d)
  const dayFog = new THREE.Color(0x9bb6d4)
  const nightFog = new THREE.Color(0x05070d)
  function applyDaylight(phase: number) {
    const { light, night } = daylight(phase)
    sky.copy(nightSky).lerp(daySky, light)
    ;(scene.fog as THREE.Fog).color.copy(nightFog).lerp(dayFog, light)
    sun.intensity = 0.15 + light * 1.5
    hemi.intensity = 0.25 + light * 0.75
    scene.environmentIntensity = 0.06 + light * 0.54 // the PBR materials' ambient light dims too
    floodlights.forEach((l) => (l.intensity = night ? 900 : 0))
    trackLamps.forEach((m) => m.color.set(night ? 0xfff1d6 : 0x8a8a80))
    for (const rc of cars.values()) rc.built.lamps.forEach((m) => m.color.set(night ? 0xfff6d8 : 0xbdbdb6))
  }
  // ?night=1 starts the cycle at midnight (handy for screenshots).
  const startPhase = new URL(location.href).searchParams.has('night') ? 0.5 : 0.12
  if (mode === 'endurance') applyDaylight(reduced ? 0.36 : startPhase)

  function frame(now: number) {
    const dt = Math.min(0.05, (now - last) / 1000)
    last = now
    if (playing) {
      clock += dt
      for (const rc of cars.values()) stepCar(rc, dt)
      if (extras?.ferris) {
        // The wheel turns slowly; its cabins stay upright.
        extras.ferris.rotor.rotation.x += dt * 0.06
        for (const g of extras.ferris.gondolas) g.rotation.x -= dt * 0.06
      }
      if (mode === 'endurance' && !reduced) applyDaylight(startPhase + clock / 240)
    }
    updateCamera(dt)
    renderer.render(scene, camera)
    requestAnimationFrame(frame)
  }
  const right = new THREE.Vector3()
  const up = new THREE.Vector3(0, 1, 0)
  const offset = new THREE.Vector3()
  function updateCamera(dt: number) {
    // The leader the cameras follow: of the class picked in the filter, else overall.
    const leaderCar = classFilter === 'all' ? feed.cars[0] : feed.cars.find((c) => c.car_class === classFilter) ?? feed.cars[0]
    const leader = cars.get(leaderCar?.id)
    const followed = focus.kind === 'chase' ? leader : focus.kind === 'car' ? cars.get(focus.id) : undefined
    for (const rc of cars.values()) rc.label.visible = rc !== followed && (classFilter === 'all' || rc.car.car_class === classFilter)
    if (followed) {
      // Keep the camera at its offset in the car's frame, so it turns with the
      // car; OrbitControls adds the viewer's drag and zoom on top.
      const f = followed.forward
      right.set(-f.z, 0, f.x)
      const car = followed.mesh.position
      offset.copy(right).multiplyScalar(followOffset.x).addScaledVector(up, followOffset.y).addScaledVector(f, followOffset.z)
      controls.target.set(car.x, car.y + 0.8, car.z)
      camera.position.copy(controls.target).add(offset)
      controls.update()
      offset.copy(camera.position).sub(controls.target)
      followOffset.set(offset.dot(right), offset.y, offset.dot(f))
    } else if (focus.kind === 'broadcast' && leader) {
      const car = leader.mesh.position
      // Each camera shows the car coming and going by; once it is 30 m
      // past, cut to the next camera ahead of it.
      const ahead = (st: Station) => wrap(st.u - leader.u) * trackLength
      const passed = (st: Station) => trackLength - ahead(st)
      const firstAhead = () => stations.reduce((best, st, i) => (ahead(st) < ahead(stations[best]) ? i : best), 0)
      const cut = stationHeld === 0 || (passed(stations[station]) > 30 && passed(stations[station]) < trackLength / 2)
      stationHeld += dt
      if (cut) {
        station = firstAhead()
        camera.position.copy(stations[station].pos)
        camLook.copy(car)
      }
      camLook.lerp(car, 1 - Math.exp(-dt * 8))
      camera.lookAt(camLook)
      // A long lens: keep the car about the same size on screen.
      const d = camera.position.distanceTo(car)
      const fov = THREE.MathUtils.clamp(THREE.MathUtils.radToDeg(2 * Math.atan(16 / d)), 6, 55)
      camera.fov += (fov - camera.fov) * (cut ? 1 : 1 - Math.exp(-dt * 4))
      camera.updateProjectionMatrix()
    } else {
      if (focus.kind === 'free' && keys.size) {
        const forward = tmp.copy(controls.target).sub(camera.position).setY(0).normalize()
        right.set(-forward.z, 0, forward.x)
        const speed = THREE.MathUtils.clamp(camera.position.distanceTo(controls.target), 20, 400) * 0.6 * dt
        offset.set(0, 0, 0)
        if (keys.has('w')) offset.add(forward)
        if (keys.has('s')) offset.sub(forward)
        if (keys.has('d')) offset.add(right)
        if (keys.has('a')) offset.sub(right)
        offset.normalize().multiplyScalar(speed)
        camera.position.add(offset)
        controls.target.add(offset)
      }
      controls.update()
    }
  }

  for (const rc of cars.values()) stepCar(rc, 0)
  setFocus({ kind: 'broadcast' })

  // Sponsor page: picking a billboard (or the round-naming gantry) in the
  // list flies the free camera in front of it and outlines it on the track.
  let outline: THREE.BoxHelper | null = null
  function showBoard(name: string, label: string) {
    const board = circuitModel?.getObjectByName(name)
    if (!board) return
    setFocus({ kind: 'free' })
    const at = board.getWorldPosition(new THREE.Vector3())
    const facing = board.getWorldDirection(new THREE.Vector3()).setY(0).normalize()
    controls.target.copy(at).add(new THREE.Vector3(0, 2.5, 0))
    // Far enough to read the sign in its surroundings (a billboard is 16 m wide).
    camera.position.copy(at).addScaledVector(facing, 78).add(new THREE.Vector3(0, 22, 0))
    controls.update()
    if (outline) scene.remove(outline)
    outline = new THREE.BoxHelper(board, 0xff5a1f)
    scene.add(outline)
    if (hint) hint.textContent = `${label} · drag to look around`
  }
  const picks = [...document.querySelectorAll<HTMLElement>('[data-sponsor-pick]')]
  const counter = document.querySelector<HTMLElement>('[data-sponsor-count]')
  const step = (delta: number) => {
    const current = picks.findIndex((el) => el.classList.contains('is-picked'))
    const next = picks[(current + delta + picks.length) % picks.length]
    next?.click()
    next?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }
  document.querySelector('[data-sponsor-prev]')?.addEventListener('click', () => step(-1))
  document.querySelector('[data-sponsor-next]')?.addEventListener('click', () => step(1))
  picks.forEach((item, index) => {
    item.addEventListener('click', () => {
      document.querySelectorAll('[data-sponsor-pick]').forEach((el) => el.classList.toggle('is-picked', el === item))
      if (counter) counter.textContent = `${index + 1} / ${picks.length}`
      const pick = item.dataset.sponsorPick!
      // The Blender signs are a frame box with a front and a back face (lib.sign).
      // Picks: naming, bridge, ferris, class:<i> (no board), or a billboard number.
      if (pick === 'bridge') {
        let last = 0
        circuitModel?.traverse((o) => { const m = o.name.match(/^Banner_(\d+)_frame$/); if (m) last = Math.max(last, Number(m[1])) })
        showBoard(`Banner_${last}_frame`, 'Footbridge')
      } else if (pick === 'ferris') showBoard('Ferris_0_frame', 'Ferris wheel')
      else if (pick === 'naming') showBoard('Banner_0_frame', 'Start/finish gantry')
      else if (pick.startsWith('banner:')) showBoard(`Banner_${Number(pick.slice(7)) + 1}_frame`, `Big banner ${Number(pick.slice(7)) + 1}`)
      else if (!pick.startsWith('class:')) showBoard(`Billboard_${pick}_frame`, `Billboard ${Number(pick) + 1}`)
    })
  })
  requestAnimationFrame(frame)
  // Development only: inspect the scene from the console.
  if (import.meta.env.DEV) Object.assign(window, { __lgpTrack: { scene, camera, controls, cars, setFocus, pause: () => { playing = false; syncPlay() } } })
}

type Station = { u: number; pos: THREE.Vector3 }

// Trackside camera stations for the broadcast view: outside the lap, beyond
// the barriers, every ~170 m. The main straight (grandstands and pit
// building on both sides) gets one camera on the pit wall at the finish line,
// which turns to follow the cars past. A station that would sit in a
// grandstand or the pit building takes the inside of the track instead
// (art/blender/circuit.py places billboards for these cameras: keep in step).
function broadcastStations(
  curve: THREE.Curve<THREE.Vector3>, centre: THREE.Vector3, width: number, avoid: THREE.Vector3[],
  pitCurve: THREE.Curve<THREE.Vector3>, startU: number, stands: number[] = [],
): Station[] {
  const pitPoints = pitCurve.getSpacedPoints(40)
  const onStraight = (p: THREE.Vector3) => pitPoints.some((q) => q.distanceTo(p) < 40)
  const count = Math.max(8, Math.round(curve.getLength() / 170))
  const stations: Station[] = []
  for (let i = 0; i < count; i++) {
    const u = (i + 0.5) / count
    const p = curve.getPointAt(u)
    if (onStraight(p)) continue
    const t = curve.getTangentAt(u)
    const n = new THREE.Vector3(-t.z, 0, t.x)
    if (n.dot(p.clone().sub(centre)) < 0) n.negate()
    const at = (side: THREE.Vector3) => p.clone().addScaledVector(side, width / 2 + 34).addScaledVector(t, 12).setY(16)
    // Never inside the pit building: take the other side of the track there.
    const spot = at(n)
    const inStand = stands.some((s) => Math.abs(wrap(s - u + 0.5) - 0.5) * curve.getLength() < 40)
    stations.push({ u: wrap(u + 12 / curve.getLength()), pos: inStand || avoid.some((g) => g.distanceTo(spot.clone().setY(g.y)) < 45) ? at(n.negate()) : spot })
  }
  const line = curve.getPointAt(startU)
  const t = curve.getTangentAt(startU)
  const toPit = new THREE.Vector3(-t.z, 0, t.x)
  const pitMid = pitCurve.getPointAt(0.5)
  if (toPit.dot(pitMid.clone().sub(line)) < 0) toPit.negate()
  stations.push({ u: startU, pos: line.clone().addScaledVector(toPit, width / 2 + 3).setY(5) })
  return stations.sort((a, b) => a.u - b.u)
}

// The car's paint from the feed; in Endurance the number panel wears the class color.
function liveryFor(car: Car): Livery {
  const paint = car.paint ?? { primary: car.livery, secondary: '#14161A', accent: '#F3F3F1', pattern: 'stripe', number: car.number, name: car.name }
  return { ...paint, panel: car.class_color } as Livery
}

function spin(rc: RaceCar, distance: number) {
  for (const w of rc.built.wheels) w.rotation.x += distance / 0.36
}

// The driver card on the right of the track: the driver, turning, and the
// product's details. One small renderer, rebuilt when the car changes.
function driverCard(stage: HTMLElement, mode: Feed['mode'], onClose: () => void, pilot: THREE.Group | null) {
  const el = stage.querySelector<HTMLElement>('[data-driver-card]')!
  const view = el.querySelector<HTMLElement>('[data-driver-render]')!
  const q = <T extends HTMLElement>(sel: string) => el.querySelector<T>(sel)!
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2))
  renderer.toneMapping = THREE.NeutralToneMapping
  view.appendChild(renderer.domElement)
  const scene = new THREE.Scene()
  scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(), 0.04).texture
  scene.add(new THREE.HemisphereLight(0xffffff, 0x404048, 1.4))
  const key = new THREE.DirectionalLight(0xffffff, 1.8)
  key.position.set(1.5, 3, 3)
  scene.add(key)
  const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 20)
  // Far enough to keep the whole driver, helmet included, in a wide panel.
  camera.position.set(0, 1.0, 4.4)
  camera.lookAt(0, 0.9, 0)
  let figure: THREE.Group | null = null
  // Cars this visitor already voted for and their own cars (/me.json, once):
  // the card's Vote button shows "Voted" or "Your car", like the running order below the track.
  const voted = new Set<number>()
  const mine = new Set<number>()
  let votedLoaded: Promise<void> | null = null
  const loadVoted = () =>
    (votedLoaded ??= fetch('/me.json', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((me: { voted?: number[]; mine?: number[] }) => {
        ;(me.voted ?? []).forEach((id) => voted.add(id))
        ;(me.mine ?? []).forEach((id) => mine.add(id))
      })
      .catch(() => undefined))
  document.addEventListener('launchgp:vote', (event) => voted.add((event as CustomEvent<{ entry: number }>).detail.entry))
  let shownId: number | null = null
  let running = false
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
  const loop = () => {
    if (!running) return
    if (figure && !reduced) figure.rotation.y += 0.008
    renderer.render(scene, camera)
    requestAnimationFrame(loop)
  }
  q('[data-driver-close]').addEventListener('click', onClose)

  return {
    show(car: Car, live = false) {
      el.hidden = false
      const w = view.clientWidth
      const h = view.clientHeight
      renderer.setSize(w, h, false)
      camera.aspect = w / Math.max(1, h)
      camera.updateProjectionMatrix()
      if (shownId !== car.id) {
        shownId = car.id
        if (figure) scene.remove(figure)
        if (car.driver) {
          figure = pilot ? dress(pilot, liveryFor(car), car.driver as never) : buildDriver(car.driver as Driver)
          figure.rotation.y = 0.35
          scene.add(figure)
        }
        const meta = [car.category, mode === 'endurance' ? `${car.class_name} class` : `#${car.number}`].filter(Boolean).join(' · ')
        q('[data-driver-meta]').textContent = meta
        const nameEl = q('[data-driver-name]')
        nameEl.textContent = car.name
        if (car.paint?.logo) {
          const logo = document.createElement('img')
          logo.className = 'driver-logo'
          logo.src = car.paint.logo
          logo.alt = ''
          nameEl.prepend(logo)
        }
        q('[data-driver-tagline]').textContent = car.tagline ?? ''
        q('[data-driver-description]').textContent = car.description ?? ''
        const stats: [string, string][] = mode === 'endurance'
          ? [['Overall', `P${car.overall}`], ['In class', `P${car.class_position}`], ['Distance', `${Math.round(car.km ?? 0).toLocaleString('en-US')} km`], ['Fuel', `${Math.round((car.fuel ?? 0) * 100)}%`]]
          : [['Position', `P${car.position ?? '–'}`], ['Votes', String(car.votes ?? 0)], ['Grid', `P${car.slot ?? '–'}`], ['Team', car.team]]
        q('[data-driver-stats]').replaceChildren(...stats.map(([dt, dd]) => {
          const div = document.createElement('div')
          const t = document.createElement('dt')
          t.textContent = dt
          const d = document.createElement('dd')
          d.textContent = dd
          div.append(t, d)
          return div
        }))
        const visit = q<HTMLAnchorElement>('[data-driver-visit]')
        const page = q<HTMLAnchorElement>('[data-driver-page]')
        visit.hidden = !car.url
        page.hidden = !car.slug
        if (car.url) {
          visit.href = car.url
          visit.rel = car.rel ?? 'nofollow noopener'
          visit.textContent = `Visit ${new URL(car.url).hostname.replace(/^www\./, '')}`
          visit.dataset.click = car.slug ?? ''
          visit.dataset.clickSource = 'track'
        }
        if (car.slug) page.href = `/p/${car.slug}`
        const vote = q<HTMLButtonElement>('[data-driver-vote]')
        const canVote = mode === 'grand_prix' && live && !car.sample
        vote.hidden = !canVote
        if (canVote) {
          vote.dataset.vote = String(car.id)
          vote.dataset.voteUrl = `/entries/${car.id}/vote`
          vote.disabled = false
          vote.textContent = 'Vote'
          vote.removeAttribute('aria-pressed')
          void loadVoted().then(() => {
            if (shownId !== car.id) return
            if (mine.has(car.id)) setMine(vote)
            else if (voted.has(car.id)) setVoted(vote)
          })
        }
        q('[data-driver-sample]').hidden = !car.sample
      }
      if (!running) {
        running = true
        loop()
      }
    },
    hide() {
      el.hidden = true
      running = false
      shownId = null
    },
  }
}

async function fetchFeed(url: string): Promise<Feed> {
  const response = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!response.ok) throw new Error(`feed ${response.status}`)
  return response.json()
}

function span(className: string, text: string) {
  const el = document.createElement('span')
  el.className = className
  el.textContent = text
  return el
}

// Did u pass `mark` going forward (with wrap-around)?
function crossed(prev: number, next: number, mark: number) {
  if (next >= prev) return prev < mark && mark <= next
  return prev < mark || mark <= next
}

const pitParamCache = new WeakMap<THREE.Vector3, number>()
function pitParamNear(circuit: Circuit, point: THREE.Vector3) {
  const cached = pitParamCache.get(point)
  if (cached !== undefined) return cached
  let best = 0.5
  let bestDistance = Infinity
  for (let i = 0; i <= 200; i++) {
    const d = circuit.pitCurve.getPointAt(i / 200).distanceToSquared(point)
    if (d < bestDistance) {
      bestDistance = d
      best = i / 200
    }
  }
  pitParamCache.set(point, best)
  return best
}

function labelSprite(text: string, color: string) {
  const canvas = document.createElement('canvas')
  canvas.width = 256
  canvas.height = 64
  const x = canvas.getContext('2d')!
  x.fillStyle = 'rgba(11,12,14,0.85)'
  x.fillRect(0, 0, 256, 64)
  x.fillStyle = color
  x.fillRect(0, 0, 10, 64)
  x.fillStyle = '#F3F3F1'
  x.textBaseline = 'middle'
  x.font = `800 38px ${FONT}`
  x.fillText(text.toUpperCase(), 24, 34, 220)
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, depthWrite: false }))
  sprite.scale.set(4.4, 1.1, 1)
  return sprite
}
